import React from 'react'
import { Table, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Spend } from '../dashboard'

const compact = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
})

export function formatMicros(micros: number, currency: string) {
    return new Intl.NumberFormat('nl-NL', {
        style: 'currency',
        currency,
        maximumFractionDigits: micros > 0 && micros < 10_000_000 ? 4 : 2,
    }).format(micros / 1_000_000)
}

type SpendTablesProps = {
    spend?: Spend
    loading?: boolean
}

// Spend per feature, per OpenAI model and per Meta pricing category, so a cost can be traced to where it came from.
export function SpendTables({ spend, loading }: SpendTablesProps) {
    const { t } = useTranslation()

    const sourceLabels: Record<string, string> = {
        webchat: t('Website chat'),
        site_try_out: t('Website assistant try-out'),
        site_voice: t('Website tone of voice'),
        interview: t('WhatsApp agents'),
        agent_preview: t('Agent preview'),
        job_offer: t('Screening questions'),
        app_actions: t('App actions'),
        legacy: t('Before recording (no price)'),
        start_message: t('Start messages'),
        agent_reply: t('Agent replies'),
        recontact: t('Recontact messages'),
        rejection: t('Rejection messages'),
        unknown: t('Sent outside Evaa'),
    }

    const paidByLabels: Record<string, string> = {
        evaa: t('Evaa'),
        customer: t('Customer'),
    }

    return (
        <div className="grid grid-cols-1 gap-4">
            <div className="min-w-0 rounded-xl border border-hairline bg-white p-5">
                <Typography.Title level={5}>{t('OpenAI per feature')}</Typography.Title>
                <Table
                    size="small"
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    pagination={false}
                    rowKey="key"
                    dataSource={spend?.openai.bySource}
                    columns={[
                        { title: t('Feature'), dataIndex: 'key', render: (key: string) => sourceLabels[key] ?? key },
                        {
                            title: t('Calls'),
                            dataIndex: 'calls',
                            align: 'right',
                            render: (value: number) => compact.format(value),
                        },
                        {
                            title: t('Searches'),
                            dataIndex: 'searches',
                            align: 'right',
                            render: (value: number) => compact.format(value),
                        },
                        {
                            title: t('Cost'),
                            dataIndex: 'costMicrodollars',
                            align: 'right',
                            render: (value: number) => formatMicros(value, 'USD'),
                        },
                    ]}
                />
            </div>

            <div className="min-w-0 rounded-xl border border-hairline bg-white p-5">
                <Typography.Title level={5}>{t('OpenAI per model')}</Typography.Title>
                <Table
                    size="small"
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    pagination={false}
                    rowKey="key"
                    dataSource={spend?.openai.byModel}
                    columns={[
                        { title: t('Model'), dataIndex: 'key' },
                        {
                            title: t('Tokens'),
                            key: 'tokens',
                            align: 'right',
                            render: (_, row) =>
                                t('{{input}} in · {{output}} out', {
                                    input: compact.format(row.inputTokens),
                                    output: compact.format(row.outputTokens),
                                }),
                        },
                        {
                            title: t('Cost'),
                            dataIndex: 'costMicrodollars',
                            align: 'right',
                            render: (value: number) => formatMicros(value, 'USD'),
                        },
                    ]}
                />
            </div>

            <div className="min-w-0 rounded-xl border border-hairline bg-white p-5">
                <Typography.Title level={5}>{t('WhatsApp per message type')}</Typography.Title>
                <Table
                    size="small"
                    scroll={{ x: 'max-content' }}
                    loading={loading}
                    pagination={false}
                    rowKey={(row) => `${row.key}-${row.paidBy}`}
                    dataSource={spend?.whatsapp.bySource}
                    columns={[
                        { title: t('Message'), dataIndex: 'key', render: (key: string) => sourceLabels[key] ?? key },
                        { title: t('Paid by'), dataIndex: 'paidBy', render: (key: string) => paidByLabels[key] ?? key },
                        {
                            title: t('Delivered'),
                            dataIndex: 'messages',
                            align: 'right',
                            render: (value: number) => compact.format(value),
                        },
                        {
                            title: t('Cost'),
                            dataIndex: 'costMicros',
                            align: 'right',
                            render: (value: number) => formatMicros(value, spend?.whatsappCurrency ?? 'EUR'),
                        },
                    ]}
                />
            </div>
        </div>
    )
}
